import React, { useState } from 'react'
import { cifraService } from '../lib/supabase'
import toast from 'react-hot-toast'

const CORES_AV = ['av-red','av-gold','av-green','av-blue']
const iniciais = (nome) => nome?.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase() || '?'
const tempoRelativo = (data) => {
  const diff = (Date.now() - new Date(data)) / 1000
  if (diff < 60) return 'agora'
  if (diff < 3600) return `${Math.floor(diff / 60)}min`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  return `${Math.floor(diff / 86400)}d`
}
const ICONES_TIPO = { pdf: '📄', imagem: '📸', link: '🔗', texto: '📝' }

// ============================================================
// MOTOR DE TRANSPOSIÇÃO DE ACORDES
// ============================================================
const NOTAS_SHARP = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B']
const NOTAS_FLAT  = ['C','Db','D','Eb','E','F','Gb','G','Ab','A','Bb','B']
const TONS_PT = {
  'Dó (C)':'C','Dó# (C#)':'C#','Ré (D)':'D','Ré# (D#)':'D#',
  'Mi (E)':'E','Fá (F)':'F','Fá# (F#)':'F#','Sol (G)':'G',
  'Sol# (G#)':'G#','Lá (A)':'A','Lá# (A#)':'A#','Si (B)':'B'
}

function notaParaIdx(nota) {
  let idx = NOTAS_SHARP.indexOf(nota)
  if (idx === -1) idx = NOTAS_FLAT.indexOf(nota)
  return idx
}

function transporAcorde(acorde, semitons) {
  if (semitons === 0) return acorde
  // Ex: C#m7, Dsus4, F#/C#, Bb
  return acorde.replace(/[A-G][#b]?/g, (nota) => {
    const idx = notaParaIdx(nota)
    if (idx === -1) return nota
    const novoIdx = ((idx + semitons) % 12 + 12) % 12
    // Usa flat se a nota original era flat ou o novo índice tem flat natural
    const usaFlat = nota.includes('b') || [1,3,6,8,10].includes(novoIdx)
    return usaFlat ? NOTAS_FLAT[novoIdx] : NOTAS_SHARP[novoIdx]
  })
}

function calcularSemitons(tomOriginal, tomDestino) {
  const orig = TONS_PT[tomOriginal] || tomOriginal
  const dest = TONS_PT[tomDestino] || tomDestino
  const idxOrig = notaParaIdx(orig)
  const idxDest = notaParaIdx(dest)
  if (idxOrig === -1 || idxDest === -1) return 0
  return ((idxDest - idxOrig) % 12 + 12) % 12
}

function transporCifra(texto, semitons) {
  if (!texto || semitons === 0) return texto
  return texto.split('\n').map(linha => {
    // Detecta se a linha é de acordes: só acordes e espaços
    const t = linha.trim()
    if (!t) return linha
    const isAcordeLinha = /^([A-G][#b]?[mM]?(7|maj7|maj|min|dim|aug|sus2|sus4|add9|6|9|11|13)?(\([^)]+\))?(\s*\/\s*[A-G][#b]?)?(\s+|$))+$/.test(t)
    if (isAcordeLinha) {
      // Transpõe cada acorde mantendo o espaçamento
      return linha.replace(/[A-G][#b]?[mM]?(7|maj7|maj|min|dim|aug|sus2|sus4|add9|6|9|11|13)?(\([^)]+\))?(\s*\/\s*[A-G][#b]?)?/g, (match) => {
        return transporAcorde(match, semitons)
      })
    }
    // Linha de letra com acordes inline (ex: "G D\nletra")
    return linha
  }).join('\n')
}

// ============================================================
// MODAL VISUALIZAÇÃO COMPLETA COM TRANSPOSIÇÃO
// ============================================================
export function VerCifraModal({ cifra, onClose, modoVocal = false, onSaveTom }) {
  const [fontSize, setFontSize] = useState(14)
  const [tomAtual, setTomAtual] = useState(cifra.tom_ultima_vez || cifra.tom_original || '')
  const [delta, setDelta] = useState(0)
  const [mostrarSeletor, setMostrarSeletor] = useState(false)
  const tomInicial = cifra.tom_ultima_vez || cifra.tom_original || ''

  const TONS_LISTA = [
    'Dó (C)','Dó# (C#)','Ré (D)','Ré# (D#)','Mi (E)','Fá (F)',
    'Fá# (F#)','Sol (G)','Sol# (G#)','Lá (A)','Lá# (A#)','Si (B)'
  ]

  // Se tom_original está definido: calcula semitons relativos a ele (comportamento original)
  // Se não está: usa delta acumulado pelos botões − e + (transposição relativa)
  const semitons = cifra.tom_original
    ? calcularSemitons(cifra.tom_original, tomAtual)
    : delta

  const cifraTransposta = transporCifra(cifra.conteudo_texto, semitons)

  function handleClose() {
    if (onSaveTom && tomAtual && tomAtual !== tomInicial) {
      onSaveTom(tomAtual)
    }
    onClose()
  }

  function renderCifra(texto) {
    if (!texto) return null
    return texto.split('\n').map((linha, i) => {
      const t = linha.trim()
      const isSecao = /^(INTRO|VERSO|REFRÃO|PRÉ|PONTE|FINAL|BRIDGE|CODA|OUTRO|CHORUS|VERSE|PRIMEIRA|SEGUNDA|INTERLÚDIO)/i.test(t)
      const isAcorde = t.length > 0 && /^([A-G][#b]?[mM]?(7|maj7|maj|min|dim|aug|sus2|sus4|add9|6|9|11|13)?(\s*\/\s*[A-G][#b]?)?(\s+|$))+$/.test(t)
      if (modoVocal && isAcorde) return null
      return (
        <div key={i} style={{
          color: isSecao ? '#58a6ff' : isAcorde ? '#00CFFF' : 'rgba(255,255,255,0.88)',
          fontWeight: isSecao || isAcorde ? 700 : 400,
          fontSize: isSecao ? 11 : fontSize,
          textTransform: isSecao ? 'uppercase' : 'none',
          letterSpacing: isSecao ? 1.5 : 0,
          marginTop: isSecao ? 10 : 0,
          lineHeight: 1.65,
          minHeight: '1em',
          whiteSpace: 'pre'
        }}>
          {linha || '\u00A0'}
        </div>
      )
    })
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.96)', zIndex: 200, display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ padding: '14px 16px', background: '#071629', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button onClick={handleClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>‹</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cifra.titulo}</div>
          {cifra.artista && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{cifra.artista}</div>}
        </div>
        <button onClick={() => setFontSize(f => Math.max(10, f - 2))} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>A-</button>
        <button onClick={() => setFontSize(f => Math.min(22, f + 2))} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>A+</button>
      </div>

      {/* Barra de tom com transposição */}
      <div style={{ padding: '10px 16px', background: '#0c2040', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: mostrarSeletor ? 10 : 0 }}>
          {/* Tom original */}
          {cifra.tom_original && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
              Original: <strong style={{ color: 'rgba(255,255,255,0.6)' }}>{cifra.tom_original}</strong>
            </span>
          )}

          <div style={{ flex: 1 }} />

          {/* Botões - e + para transpor */}
          <button onClick={() => {
            const idx = TONS_LISTA.indexOf(tomAtual)
            const novoIdx = idx > 0 ? idx - 1 : 11
            setTomAtual(TONS_LISTA[novoIdx])
            if (!cifra.tom_original) setDelta(d => ((d - 1) % 12 + 12) % 12)
          }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>

          {/* Tom atual — clica para abrir seletor */}
          <button onClick={() => setMostrarSeletor(s => !s)} style={{ background: 'rgba(0,207,255,0.15)', border: '2px solid rgba(0,207,255,0.5)', color: '#00CFFF', padding: '5px 14px', borderRadius: 50, fontSize: 13, fontWeight: 800, cursor: 'pointer', minWidth: 90, textAlign: 'center', fontFamily: 'Montserrat,sans-serif' }}>
            🎵 {tomAtual || 'Tom'}
          </button>

          <button onClick={() => {
            const idx = TONS_LISTA.indexOf(tomAtual)
            const novoIdx = idx < 11 ? idx + 1 : 0
            setTomAtual(TONS_LISTA[novoIdx])
            if (!cifra.tom_original) setDelta(d => (d + 1) % 12)
          }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
        </div>

        {/* Seletor de tom expandido */}
        {mostrarSeletor && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 4 }}>
            {TONS_LISTA.map(t => (
              <button key={t} onClick={() => { setTomAtual(t); setMostrarSeletor(false) }}
                style={{ padding: '5px 10px', borderRadius: 50, border: `1px solid ${t === tomAtual ? '#00CFFF' : 'rgba(255,255,255,0.15)'}`, background: t === tomAtual ? 'rgba(0,207,255,0.2)' : 'rgba(255,255,255,0.05)', color: t === tomAtual ? '#00CFFF' : 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: t === tomAtual ? 700 : 400, cursor: 'pointer' }}>
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Indicador de transposição */}
        {semitons !== 0 && (
          <div style={{ fontSize: 11, color: 'rgba(0,207,255,0.7)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>↕ {semitons > 6 ? semitons - 12 : semitons} semitons</span>
            <button onClick={() => { setTomAtual(cifra.tom_original || cifra.tom_ultima_vez || ''); setDelta(0) }}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
              Resetar
            </button>
          </div>
        )}
      </div>

      {/* Conteúdo scrollável */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', WebkitOverflowScrolling: 'touch' }}>
        {cifra.arquivo_url && cifra.arquivo_tipo === 'imagem' && (
          <img src={cifra.arquivo_url} alt={cifra.titulo} style={{ width: '100%', borderRadius: 12, marginBottom: 16 }} />
        )}
        {cifra.arquivo_url && cifra.arquivo_tipo === 'pdf' && (
          <a href={cifra.arquivo_url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, background: 'rgba(255,140,0,0.1)', borderRadius: 12, border: '1px solid rgba(255,140,0,0.3)', color: '#FF8C00', textDecoration: 'none', marginBottom: 16, fontWeight: 600 }}>
            📄 Abrir PDF completo
          </a>
        )}
        {cifra.link_externo && (
          <a href={cifra.link_externo} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: 'rgba(255,255,255,0.05)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', color: '#00CFFF', textDecoration: 'none', marginBottom: 14, fontSize: 13 }}>
            🔗 Ver fonte original
          </a>
        )}

        {cifraTransposta ? (
          <div style={{ fontFamily: "'Courier New',monospace", background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '16px 14px', border: '1px solid rgba(255,255,255,0.07)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {renderCifra(cifraTransposta)}
          </div>
        ) : !cifra.arquivo_url && !cifra.link_externo ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.3)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎵</div>
            Nenhum conteúdo de cifra disponível.
          </div>
        ) : null}

        <div style={{ height: 40 }} />
      </div>

      {/* Rodapé */}
      <div style={{ padding: '10px 16px 24px', background: '#071629', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8, flexShrink: 0 }}>
        {cifra.link_externo && (
          <a href={cifra.link_externo} target="_blank" rel="noreferrer" style={{ flex: 1, padding: 11, borderRadius: 10, background: 'rgba(0,207,255,0.08)', border: '1px solid rgba(0,207,255,0.25)', color: '#00CFFF', textDecoration: 'none', textAlign: 'center', fontSize: 13, fontWeight: 600 }}>
            🔗 Fonte
          </a>
        )}
        {cifra.arquivo_url && (
          <a href={cifra.arquivo_url} download target="_blank" rel="noreferrer" style={{ flex: 1, padding: 11, borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', textAlign: 'center', fontSize: 13 }}>
            ⬇ Baixar
          </a>
        )}
        <button onClick={handleClose} style={{ flex: 1, padding: 11, borderRadius: 10, background: '#FF8C00', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          ✓ Fechar
        </button>
      </div>
    </div>
  )
}

// ============================================================
// CARD DO FEED
// ============================================================
export default function CifraCard({ cifra, userId, onVerTom, onCurtir, onDeletada, modoVocal = false }) {
  const [curtido, setCurtido] = useState(false)
  const [numCurtidas, setNumCurtidas] = useState(cifra.curtidas?.[0]?.count || 0)
  const [verCifra, setVerCifra] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deletando, setDeletando] = useState(false)

  const autor = cifra.profiles
  const avatarIdx = (autor?.nome?.charCodeAt(0) || 0) % CORES_AV.length
  const isAutor = autor?.id === userId

  async function handleCurtir(e) {
    e.stopPropagation()
    const novo = !curtido
    setCurtido(novo)
    setNumCurtidas(n => novo ? n + 1 : n - 1)
    try { await onCurtir() } catch { setCurtido(!novo); setNumCurtidas(n => novo ? n - 1 : n + 1) }
  }

  async function handleDeletar(e) {
    e.stopPropagation()
    if (!confirmDelete) {
      setConfirmDelete(true)
      setTimeout(() => setConfirmDelete(false), 3000)
      return
    }
    setDeletando(true)
    try {
      const { error } = await cifraService.deletarCifra(cifra.id, userId)
      if (error) throw error
      toast.success('Cifra apagada')
      onDeletada(cifra.id)
    } catch (e) {
      toast.error('Erro ao apagar: ' + e.message)
    } finally {
      setDeletando(false)
      setConfirmDelete(false)
    }
  }

  return (
    <>
      <div className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setVerCifra(true)}>
        <div style={{ height: 3, background: 'linear-gradient(90deg, var(--brand2), var(--gold))' }} />
        <div style={{ padding: 16 }}>

          {/* Autor */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div className={`avatar av-sm ${CORES_AV[avatarIdx]}`}>
              {autor?.avatar_url ? <img src={autor.avatar_url} alt="" /> : iniciais(autor?.nome)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{autor?.nome || 'Músico'}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{tempoRelativo(cifra.publicado_em)}</div>
            </div>
            {cifra.arquivo_tipo && <span style={{ fontSize: 18 }}>{ICONES_TIPO[cifra.arquivo_tipo] || '🎵'}</span>}
            {isAutor && (
              <button onClick={handleDeletar} disabled={deletando}
                style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${confirmDelete ? 'var(--brand2)' : 'rgba(255,140,0,0.2)'}`, background: confirmDelete ? 'rgba(255,140,0,0.15)' : 'transparent', color: confirmDelete ? 'var(--brand2)' : 'rgba(255,140,0,0.5)', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                {deletando ? '...' : confirmDelete ? '⚠️ Confirmar?' : '🗑'}
              </button>
            )}
          </div>

          {/* Título */}
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 700, marginBottom: 3, lineHeight: 1.2 }}>{cifra.titulo}</div>
          {cifra.artista && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>{cifra.artista}</div>}

          {/* Tags */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {cifra.tom_ultima_vez && <span className="badge badge-tom">Tom: {cifra.tom_ultima_vez}</span>}
            {cifra.tom_original && cifra.tom_original !== 'Não identificado' && cifra.tom_original !== cifra.tom_ultima_vez && (
              <span className="badge" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--muted)', border: '1px solid var(--card-border)', fontSize: 11 }}>Original: {cifra.tom_original}</span>
            )}
            {cifra.arquivo_tipo && <span className="badge badge-tipo">{ICONES_TIPO[cifra.arquivo_tipo]} {cifra.arquivo_tipo}</span>}
            {cifra.genero && cifra.genero !== 'Outro' && <span className="badge badge-genero">{cifra.genero}</span>}
          </div>

          {/* Preview */}
          {cifra.conteudo_texto && (
            <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '10px 12px', fontFamily: "'Courier New',monospace", fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 12, maxHeight: 56, overflow: 'hidden', position: 'relative' }}>
              {cifra.conteudo_texto.split('\n').slice(0, 3).map((l, i) => <div key={i}>{l || ' '}</div>)}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 24, background: 'linear-gradient(transparent, rgba(10,15,30,0.9))' }} />
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--brand2)', textAlign: 'center', marginBottom: 10, opacity: 0.8 }}>
            👆 Toque para ver e transpor a cifra
          </div>

          {/* Ações */}
          <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--card-border)', paddingTop: 12 }} onClick={e => e.stopPropagation()}>
            <button onClick={handleCurtir} style={{ flex: 1, padding: '9px', borderRadius: 8, border: `1px solid ${curtido ? 'var(--brand2)' : 'var(--card-border)'}`, background: curtido ? 'rgba(255,140,0,0.1)' : 'var(--card-bg)', color: curtido ? 'var(--brand2)' : 'var(--muted)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              {curtido ? '❤️' : '🤍'} {numCurtidas > 0 ? numCurtidas : ''}
            </button>
            <button onClick={e => { e.stopPropagation(); onVerTom() }} style={{ flex: 2, padding: '9px', borderRadius: 8, border: '1px solid rgba(0,207,255,0.3)', background: 'rgba(0,207,255,0.08)', color: 'var(--gold)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              🎵 Tom / Registrar
            </button>
            <button onClick={e => { e.stopPropagation(); setVerCifra(true) }} style={{ flex: 2, padding: '9px', borderRadius: 8, border: '1px solid rgba(255,140,0,0.3)', background: 'rgba(255,140,0,0.08)', color: 'var(--brand2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              👁 Ver cifra
            </button>
          </div>
        </div>
      </div>

      {verCifra && <VerCifraModal cifra={cifra} onClose={() => setVerCifra(false)} modoVocal={modoVocal}
        onSaveTom={async (tom) => { try { await cifraService.salvarTomUltimaVez(cifra.id, tom) } catch {} }} />}
    </>
  )
}
